import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { UserStudyPermissionsAPI } from '../api/client';
import { useAuth } from '../hooks/useAuth';

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [pagePermissions, setPagePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      setPermissions(null);
      setPagePermissions({});
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Add timeout to prevent hanging forever
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      const data = await Promise.race([
        UserStudyPermissionsAPI.myPermissions(),
        timeoutPromise
      ]);

      setPermissions(data);
      setPagePermissions(data.page_permissions || {});
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setError(err.message || 'Failed to load permissions');
      // Set default permissions on error (conservative approach)
      setPagePermissions({
        experiments: false,
        feature_extraction: false,
        studies: true,  // Allow basic access to studies
        downloads: true, // Allow basic access to downloads
        admin: false,
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const hasPagePermission = useCallback((pageCode) => {
    if (loading) return false;
    return pagePermissions[pageCode] === true;
  }, [pagePermissions, loading]);

  const value = useMemo(() => ({
    permissions,
    pagePermissions,
    loading,
    error,
    hasPagePermission,
    reloadPermissions: loadPermissions,
  }), [permissions, pagePermissions, loading, error, hasPagePermission, loadPermissions]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
